import React, {useEffect, useState, useRef, useCallback} from 'react';
import {Flex, Image, useTheme, Center, Spinner, Box, Text, Stack, Link} from '@chakra-ui/react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import Header from './Header';
import {Event} from '../../types/article';
import BackButton from '../../components/BackButton';
import DefaultEventImage from '../../assets/images/default_event.png';
import EventAbout from './About';
import FallbackImg from '../../assets/images/fallback.jpg';
import {useFetchEvent} from '../../hooks/useEvent';
import {useOffers} from '../../hooks/useOffers';
import LocationDisabled from '../../components/LocationDisabled';
import EventTooltips from '../../components/TutorialTooltips/EventTooltips';
import TutorialTooltips from '../../components/TutorialTooltips';
import {useFetchUserTickets} from '../../hooks/useUserTickets';
import {filter} from 'lodash';
import useAuth from '../../hooks/useAuth';
import EventHelmet from './EventHelmet';
import useTrackEvent from '../../hooks/useTrackEvent';
import EventTabs from './Tabs';
import MediaGallery from '../../components/MediaGallery';
import {H1, TextG10, TextG16, TextA18, H2} from '../../components/typography';
import Icon from '../../components/Icon';
import {logEventViewItem} from '../../utils/analytics';
import {canGoBack} from '../../utils/history';
import PartnerCard from '../../components/Articles/PartnerCard';
import MediaEditor from "../../components/MediaEditor";
import useCheckIn from '../../hooks/useCheckIn';
import useActiveRedemptions from '../../hooks/useActiveRedemptions';

const Event = ({onClose}: {onClose?: () => void}) => {
  const {eventId} = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchEvent = useFetchEvent(Number(eventId || searchParams.get('id')));
  const [locationDisabled, setLocationDisabled] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const isTutorial = !!searchParams.get('tutorial');
  const tutorialViewed = localStorage.getItem('events_tutorial_viewed');
  const viewItemTracked = useRef(false);
  const {offers, offersIsLoading} = useOffers({
    articleType: 'eventId',
    articleId: eventId || searchParams.get('id'),
  });
  const {addUserEvent} = useTrackEvent();

  const {user} = useAuth();
  const {eventCheckIn} = useCheckIn();
  const {activeRedemptions} = useActiveRedemptions();

  const fetchTickets = useFetchUserTickets(!!user);

  const event: Event = fetchEvent.data?.data;
  const tickets = fetchTickets.data?.data;

  const eventTickets = filter(
    tickets,
    (ticket) => ticket.event_id == (eventId || searchParams.get('id'))
  );

  const userHasTickets = eventTickets && eventTickets.length > 0;
  const isLoading =
    fetchEvent.isLoading || fetchTickets.isLoading || offersIsLoading || event?.is_meetup;

  // Check if event description contains [[specialHeader]] keyword
  const hasSpecialHeader = event?.description?.includes('[[specialHeader]]') || false;

  // Check if event description contains [[farmersMarketHeader]] keyword
  const hasFarmersMarketHeader = event?.description?.includes('[[farmersMarketHeader]]') || false;

  function trackDeepLinkAppUserEvent() {
    const source = searchParams.get('source');
    if (source) {
      const sourceId = searchParams.get('source_id');
      addUserEvent({
        type: 'deep_link',
        event_name: 'event_deep_link',
        data: {
          source: source,
          source_id: sourceId ? Number(sourceId) : undefined,
        },
      });
      // Remove from URL so it is only logged once
      let url = new URL(location.href);
      url.searchParams.delete('source');
      url.searchParams.delete('source_id');
      navigate(url, {replace: true});
    }
  }

  useEffect(() => {
    if (event) {
      trackDeepLinkAppUserEvent();
      
      if (!viewItemTracked.current) {
        logEventViewItem(event);
        viewItemTracked.current = true;
      }

      if (event.is_meetup) {
        if (searchParams.has('id') && onClose) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('articleType', 'meet-up');
          setSearchParams(newParams, {replace: true});
          return;
        }
        const queryString = searchParams.toString();
        navigate(`/meet-up/${event.id}${queryString ? `?${queryString}` : ''}`, {replace: true});
        return;
      }
      if (!event.allow_check_in) {
        return;
      }
      // Tooltip highlights won't work correctly inside of a modal page,
      // so if this is a modal page navigate to the event's full page
      if (event && !!searchParams.has('id') && !!searchParams.get('tutorial')) {
        navigate(`/event/${event.id}?tutorial=t`, {replace: true});
        return;
      }

      setShowTutorial(isTutorial);

      if (isTutorial) window.scrollTo(0, 0);
      localStorage.setItem('events_tutorial_viewed', 'true');
    }
  }, [isTutorial, event]);

  useEffect(() => {
    if (!tutorialViewed) {
      const url = new URL(location.href);
      if (!url.searchParams.get('tutorial')) url.searchParams.append('tutorial', 't');
      const path = url.pathname + url.search;
      navigate(path, {replace: true});
    }
  }, []);

  const navigateBack = React.useCallback(() => {
    if (searchParams.has('id') && onClose) {
      onClose();
      return;
    }
    
    canGoBack() ? navigate(-1) : navigate('/home');
  }, []);

  // Bypass location check and proceed with check-in using sdv (skip distance verification)
  const handleBypassLocationCheckIn = useCallback(() => {
    if (!event) return;
    
    eventCheckIn({
      event: event,
      eventOffers: offers,
      activeRedemptions: activeRedemptions,
      latitude: undefined,
      longitude: undefined,
      isPublic: true,
      sdv: true,
      qr_code_id: undefined,
    });
    setLocationDisabled(false);
  }, [event, offers, activeRedemptions, eventCheckIn]);

  if (isLoading || event === undefined)
    return (
      <Center style={centerCss}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );

  if (locationDisabled) return <LocationDisabled onBypassLocation={handleBypassLocationCheckIn} entityName={event?.title} />;

  return (
    <>
      <EventHelmet event={event} />
      {showTutorial && <TutorialTooltips tooltipFunc={EventTooltips} />}
      <Flex
        position="relative"
        height={hasSpecialHeader || hasFarmersMarketHeader ? '210px' : '250px'}>
        <BackButton onClick={navigateBack} m={'.75rem'} pos="relative" />
        {hasSpecialHeader ? (
          <>
            {event.image_url && <MediaGallery media={event.media} />}
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              bg="blackAlpha.600"
              borderBottomRadius="14px"
              zIndex="1"
            />
            <Center position="absolute" top="0" left="0" width="100%" height="100%" zIndex="2">
              <Box borderRadius="12px" px="3" py="1.5" backdropFilter="blur(4px)">
                <H1
                  my="0 !important"
                  color="white"
                  textAlign="center"
                  fontWeight="900"
                  fontSize={['28px', '32px', '36px']}
                  textShadow="3px 3px 6px rgba(0,0,0,0.9)"
                  letterSpacing="1px">
                  {event.title}
                </H1>
              </Box>
            </Center>
          </>
        ) : hasFarmersMarketHeader ? (
          <>
            <Box
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="100%"
              bg="rgba(0,0,0,0.6)"
              borderBottomRadius="14px"
              zIndex="1"
            />
            <Center
              position="absolute"
              top="0"
              left="0"
              width="100%"
              height="fit-content"
              zIndex="2"
              alignItems="flex-start"
              mt="48px">
              <Flex direction="column" align="center" justify="center">
                <Flex align="center" mb="2" gap={2} justify="center">
                  {/* <Icon
                    iconName="fi-sr-compass-alt"
                    style={{
                      color: theme.colors.rose,
                      fontSize: '30px',
                    }}
                  /> */}

                  <H1
                    my="0 !important"
                    color="white"
                    textAlign="center"
                    fontWeight="900"
                    fontSize="22px"
                    textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                    letterSpacing="0.5px"
                    maxWidth="90%">
                    NORTH PARK FARMERS MARKET AND NEARBY EXCITEMENT
                  </H1>
                </Flex>
                <TextG16
                  color="white"
                  textAlign="center"
                  fontSize="16px"
                  fontWeight="500"
                  textShadow="1px 1px 2px rgba(0,0,0,0.8)"
                  maxWidth="90%">
                  Discover hidden gems, local meet-ups, and everything that makes this neighborhood
                  special.
                </TextG16>
              </Flex>
            </Center>
          </>
        ) : (
          <>
            <MediaGallery media={event.media} />
            <MediaEditor
              media={event.media}
              entityType="events"
              entityId={event.id}
              refetch={fetchEvent.refetch}
            />
          </>
        )}
      </Flex>
      {!hasSpecialHeader && !hasFarmersMarketHeader ? (
        <>
          <Header
            event={event}
            eventOffers={offers}
            refetchEvent={fetchEvent.refetch}
            disableLocation={() => setLocationDisabled(true)}
          />
          <EventTabs event={event} eventOffers={offers} refetchEvent={fetchEvent.refetch} />
        </>
      ) : (
        // <FarmersMarketContent event={event} />
        <EventAbout event={event} eventOffers={offers} refetchEvent={fetchEvent.refetch}/>
      )}
      {event.partners && event.partners.length > 0 && (
        <Stack gap="0rem" p="1rem" pb="50px" pt={0} textAlign="left">
          <H2 my={0}>Partners</H2>
          {event.partners.map((partner) => (
            <PartnerCard key={partner.id} partner={partner} />
          ))}
        </Stack>
      )}
    </>
  );
};

export default Event;

const centerCss: React.CSSProperties = {
  minHeight: '100vh',
};
